import React from 'react';
import { TopicCard } from './TopicCard';
import { BookOpen, X } from 'lucide-react';
import { planSecondaryOutlineCompactButtonClass } from './LessonsPage/planFloatingActionsTheme';
import { maxTopicsPerUnit } from './LessonsPage/constants';
import { getCreateUnitCompatibilityFromTreeSelection } from './LessonsPage/utils/topicUtils';
import { extractTopicMetadata } from './LessonsPage/utils/curriculumUtils';

interface LessonsPanelProps {
  selectedTopics: any[];
  expandedCards: Set<string>;
  onToggleCard: (topicId: string) => void;
  onRemoveTopic: (index: number) => void;
  onClearAll: () => void;
  buildBreadcrumb: (topic: any) => string;
  isStudentCurriculum?: boolean;
  studentCurriculumData?: any;
  getProviderName?: (providerKey: string | null | undefined) => string;
  units?: any[]; // Units data to get assessment scores
  passFailThreshold?: number | null; // Student's pass/fail threshold
  onLoadTopicDetails?: (topicId: string, providerKey?: string) => void;
  loadingTopicDetails?: Set<string>;
  curriculumProviderKey?: string;
  studentId?: string | null;
  /** First name for empty-state copy (e.g. "Sam's Curriculum"). */
  selectedStudentFirstName?: string;
}

export function LessonsPanel({
  selectedTopics,
  expandedCards,
  onToggleCard,
  onRemoveTopic,
  onClearAll,
  buildBreadcrumb,
  isStudentCurriculum = false,
  studentCurriculumData,
  getProviderName,
  units = [],
  passFailThreshold = null,
  onLoadTopicDetails,
  loadingTopicDetails = new Set(),
  curriculumProviderKey,
  studentId = null,
  selectedStudentFirstName = ''
}: LessonsPanelProps) {
  const studentCurriculumLabel =
    selectedStudentFirstName.trim() !== ''
      ? `${selectedStudentFirstName.trim()}'s Curriculum`
      : 'Curriculum';

  const createUnitIncompatibilityNote = (() => {
    if (!isStudentCurriculum || selectedTopics.length === 0) return null;
    const compatibility = getCreateUnitCompatibilityFromTreeSelection(
      studentCurriculumData,
      selectedTopics,
      extractTopicMetadata,
      maxTopicsPerUnit
    );
    if (compatibility.canCreateUnit) return null;
    return compatibility.reason ?? 'This selection cannot be used to create a unit.';
  })();

  const renderTopicCard = (topic: any, index: number) => {
    const topicId = topic.e179_TopicId || topic.e180_TopicId || `topic-${index}`;
    const isExpanded = expandedCards.has(topicId);
    
    const toggleExpanded = () => {
      onToggleCard(topicId);
    };

    const removeTopic = () => {
      onRemoveTopic(index);
    };

    return (
      <TopicCard
        key={topicId}
        topic={topic}
        index={index}
        isExpanded={isExpanded}
        onToggleExpanded={toggleExpanded}
        onRemove={removeTopic}
        buildBreadcrumb={buildBreadcrumb}
        getProviderName={getProviderName}
        units={units}
        passFailThreshold={passFailThreshold}
        onLoadTopicDetails={onLoadTopicDetails}
        loadingTopicDetails={loadingTopicDetails}
        curriculumProviderKey={curriculumProviderKey}
        studentId={studentId}
      />
    );
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {selectedTopics.length > 0 ? (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900 shrink-0">
              Selected Topics ({selectedTopics.length})
            </h2>
            {createUnitIncompatibilityNote && (
              <p className="min-w-0 flex-1 text-sm text-abbey leading-snug">
                {createUnitIncompatibilityNote}
              </p>
            )}
            <button
              type="button"
              onClick={onClearAll}
              className={`${planSecondaryOutlineCompactButtonClass} shrink-0`}
              title="Clear the current topic selection"
            >
              <X size={14} />
              <span>Clear All</span>
            </button>
          </div>
          <div className="grid gap-6">
            {selectedTopics.map((topic, index) => renderTopicCard(topic, index))}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            {isStudentCurriculum ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Create Units</h3>
                <div className="text-gray-500 mb-6 space-y-4">
                  <p>Use the Create Unit button above the Curriculum List to the left to build a unit.</p>
                  <p>
                    You can also Expand a Subject and Select compatible Topics to perform specific actions such as
                    Creating an Assessment or Removing Topics.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg text-gray-900 mb-6">
                  <span className="font-medium">Select Topics</span> to add them to {studentCurriculumLabel}
                </h3>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
